import apiClient from './client';

export enum TransactionType {
  RESTOCK = 'RESTOCK',
  USAGE = 'USAGE',
  ADJUSTMENT = 'ADJUSTMENT',
  COUNT = 'COUNT',
  WASTE = 'WASTE'
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED'
}

export interface TransactionMetadata {
  attachments?: string[];
  tags?: string[];
  reason?: string;
  location?: string;
  operator?: string;
  notes?: string;
  [key: string]: unknown;
}

export interface FeedStockTransaction {
  id: string;
  feedStockId: string;
  feedStock?: {
    id: string;
    name: string;
    type: string;
    unit: string;
  };
  type: TransactionType;
  status: TransactionStatus;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
  transactionDate: string;
  effectiveDate?: string;
  metadata?: TransactionMetadata;
  referenceNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedStockTransactionDto {
  feedStockId: string;
  type: TransactionType;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
  transactionDate: string;
  effectiveDate?: string;
  metadata?: TransactionMetadata;
  referenceNumber?: string;
  notes?: string;
}

export interface UpdateFeedStockTransactionDto {
  status?: TransactionStatus;
  quantity?: number;
  unitPrice?: number;
  totalPrice?: number;
  effectiveDate?: string;
  metadata?: TransactionMetadata;
  referenceNumber?: string;
  notes?: string;
}

export interface FeedStockTransactionFilterDto {
  feedStockId?: string;
  type?: TransactionType;
  status?: TransactionStatus;
  startDate?: string;
  endDate?: string;
}

// API Functions
export const feedStockTransactionApi = {
  // Get all transactions with optional filters
  getAll: async (filters?: FeedStockTransactionFilterDto): Promise<FeedStockTransaction[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await apiClient.get<FeedStockTransaction[]>(`feed-stock/transactions${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  },

  // Get transaction by ID
  getById: async (id: string): Promise<FeedStockTransaction> => {
    const response = await apiClient.get<FeedStockTransaction>(`feed-stock/transactions/${id}`);
    return response.data;
  },

  // Create new transaction
  create: async (data: CreateFeedStockTransactionDto): Promise<FeedStockTransaction> => {
    const response = await apiClient.post<FeedStockTransaction>('feed-stock/transactions', data);
    return response.data;
  },

  // Update transaction
  update: async (id: string, data: UpdateFeedStockTransactionDto): Promise<FeedStockTransaction> => {
    const response = await apiClient.put<FeedStockTransaction>(`feed-stock/transactions/${id}`, data);
    return response.data;
  },

  // Cancel transaction
  cancel: async (id: string): Promise<FeedStockTransaction> => {
    const response = await apiClient.delete<FeedStockTransaction>(`feed-stock/transactions/${id}`);
    return response.data;
  },

  // Get transactions for specific feed stock
  getByFeedStock: async (feedStockId: string, filters?: Omit<FeedStockTransactionFilterDto, 'feedStockId'>): Promise<FeedStockTransaction[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await apiClient.get<FeedStockTransaction[]>(`feed-stock/transactions/feed-stock/${feedStockId}${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  }
}; 